import {FC} from 'react'

import cn from 'classnames'
import {observer} from 'mobx-react-lite'

import PreviewWaterMark from 'components/blocks/documents/DocumentPaystub/templates/TemplateComponents/PreviewWaterMark'
import {termsOptions} from 'models/Invoice/Invoice.configs'
import {ITemplateProps} from 'src/types'
import {getFormattedDate, parseNumber} from 'src/utils'

/** InvoiceTemplate1 block component */
const InvoiceTemplate1: FC<ITemplateProps<docs.IInvoiceData>> = ({
	className,
	data,
	isPreviewOnly = true,
	isPreview = false,
}) => {
	const shouldShowDue = data?.terms !== 'on_receipt' && data?.terms !== 'none'
	return (
		<div className={cn('invoice', 'invoice-template', className)} id="invoice_preview">
			{isPreviewOnly && <PreviewWaterMark isCheckMark={false} />}
			<div className="invoice__inner">
				<div className="invoice-preview">
					<div className="invoice__title">
						<div className="invoice__title-inner">
							{data?.formName && <h2 className="invoice__title-text">{data?.formName}</h2>}
						</div>
						<div className="invoice__logo">
							<div className="invoice__logo-inner">
								{data?.logo ? (
									/* eslint-disable-next-line @next/next/no-img-element */
									<img className="invoice__logo-img" src={data?.logo} alt="Logo" />
								) : (
									!isPreview && (
										/* eslint-disable-next-line @next/next/no-img-element */
										<img
											className="invoice__logo-img"
											src={`http://placehold.jp/4673EC/ffffff/1707x805.png?text=Logo`}
											alt="Logo"
										/>
									)
								)}
							</div>
						</div>
					</div>
					<div className="invoice-detail-header">
						<div className="invoice__row">
							<div className="invoice__col">
								<h3 className="mb-1">From</h3>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.from?.name && <h2 className="fw-normal mb-0">{data?.from?.name}</h2>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.from?.email && <span>{data?.from?.email}</span>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.from?.address1 && <span>{data?.from?.address1}, </span>}
										{data?.from?.address2 && <span>{data?.from?.address2}, </span>}
										{data?.from?.city && <span>{data?.from?.city}, </span>}
										{data?.from?.state && <span>{data?.from?.state}, </span>}
										{data?.from?.zip && <span>{data?.from?.zip}</span>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.from?.phone && (
											<>
												<span>P:&nbsp;</span>
												<span>{data?.from?.phone}</span>
											</>
										)}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.from?.businessNumber && <span>{data?.from?.businessNumber}</span>}
									</div>
								</div>
							</div>
							<div className="invoice__col">
								<h3 className="mb-1">For</h3>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.to?.name && <h2 className="fw-normal mb-0">{data?.to?.name}</h2>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.to?.email && <span>{data?.to?.email}</span>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.to?.address1 && <span>{data?.to?.address1}, </span>}
										{data?.to?.address2 && <span>{data?.to?.address2}, </span>}
										{data?.to?.city && <span>{data?.to?.city}, </span>}
										{data?.to?.state && <span>{data?.to?.state}, </span>}
										{data?.to?.zip && <span>{data?.to?.zip}</span>}
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field">
										{data?.to?.phone && (
											<>
												<span>P:&nbsp;</span>
												<span>{data?.to?.phone}</span>
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
					<hr className="invoice-spacing" />
					<div className="invoice-detail-terms mb-1">
						<div className="invoice__row ">
							<div className="invoice__col">
								<div className="invoice__data">
									<div className="invoice__field width20">
										<span>Number:</span>
									</div>
									<div className="invoice__field width80">
										<span className="invoice_number_text">{data?.number}</span>
									</div>
								</div>
								<div className="invoice__data">
									<div className="invoice__field width20">
										<span>Date:</span>
									</div>
									<div className="invoice__field width80">
										<span className="invoice_date_text">
											{data?.date && getFormattedDate(data?.date)}
										</span>
									</div>
								</div>
								<div className="invoice__data ">
									<div className="invoice__field width20">
										<span>Terms:</span>
									</div>
									<div className="invoice__field width80">
										<span className="invoice_terms_text">
											{termsOptions.find((term) => term.value === data?.terms)?.name}
										</span>
									</div>
								</div>
								{shouldShowDue && (
									<div className="invoice__data ">
										<div className="invoice__field width20">
											<span>Due:</span>
										</div>
										<div className="invoice__field width80">
											<span className="invoice_due_text">
												{data?.due && getFormattedDate(data?.due)}
											</span>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="invoice-list">
						<table className="invoice-list__table invoice-table">
							<thead className="invoice-table__thead">
								<tr>
									<th className="invoice-table__title invoice-table__title--description">
										Description
									</th>
									<th className="invoice-table__title">Rate</th>
									<th className="invoice-table__title">Qty</th>
									<th className="invoice-table__title">Amount</th>
								</tr>
							</thead>
							<tbody className="invoice-table__body invoice-items">
								{data?.items?.map((item, idx) => (
									<tr key={`${item.description}_${idx}`} className="invoice-items__row with-tax">
										<td className="invoice-items__cell--summary">
											<h4 className="fw-normal mb-0">{item.description}</h4>
											{item.details && <span>{item.details}</span>}
										</td>
										<td className="invoice-items__cell invoice-items__cell--rate">
											<span>$</span>
											{item.rate}
										</td>
										<td className="invoice-items__cell invoice-items__cell--quantity">
											{item.qty}
										</td>
										<td className="invoice-items__cell invoice-items__cell--amount">
											<span>$</span>
											{item.amount?.toFixed(2)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<hr />
					<div className="invoice-totals mb-1">
						<div className="invoice-totals__row">
							<div className="invoice-totals__label">Subtotal</div>
							<div className="invoice-totals__value">
								$<span>{data?.subtotal?.toFixed(2)}</span>
							</div>
						</div>
						{data?.discountType !== 'none' && (
							<div className="invoice-totals__row ">
								<div className="invoice-totals__label">
									Discount{' '}
									{data?.discountType === 'percent' && (
										<span>({parseNumber(data?.discountRate)}%)</span>
									)}
								</div>
								<div className="invoice-totals__value">
									- $<span>{data?.discount?.toFixed(2)}</span>
								</div>
							</div>
						)}
						<div className="invoice-totals__row">
							<div className="invoice-totals__label">Tax ({parseNumber(data?.taxRate)}%)</div>
							<div className="invoice-totals__value">
								<span>
									{(data?.tax || 0) < 0 ? (
										<>
											<span>- $</span> <span>{Math.abs(data?.tax || 0).toFixed(2)}</span>
										</>
									) : (
										<>
											<span>+ $</span> <span>{Math.abs(data?.tax || 0).toFixed(2)}</span>
										</>
									)}
								</span>
							</div>
						</div>
						<div className="invoice-totals__row">
							<div className="invoice-totals__label">Total</div>
							<div className="invoice-totals__value">
								$<span>{data?.total?.toFixed(2)}</span>
							</div>
						</div>
						<div className="invoice-totals__row">
							<div className="invoice-totals__label">
								<strong>Balance Due</strong>
							</div>
							<strong className="invoice-totals__value">
								$<span>{data?.total?.toFixed(2)}</span>
							</strong>
						</div>
					</div>
					<hr />
					<div className="invoice-detail-notes">
						{data?.notes && (
							<p className="invoice-notes">
								Notes - <span className="invoice_notes_text">{data?.notes}</span>
							</p>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

InvoiceTemplate1.displayName = 'InvoiceTemplate1'

export const BackendInvoiceTemplate1 = InvoiceTemplate1

export default observer(InvoiceTemplate1)
